import { Fragment } from 'react';
import { cn } from '../../../../lib/utils';
import { primaryText, secondaryText, semiBoldText, smallTextSize } from '../../../../styling/stylingHelper';
import type { ClientTrackerResponse } from '../../../../services/admin/clientAnalyticsService';
import { sumMonths, sumRestaurants, formatEuro, formatPct, type Lens, type MatrixColumn } from './trackerUtils';

interface RevenueMatrixViewProps {
  data: ClientTrackerResponse;
  columns: MatrixColumn[];
  lens: Lens;
}

const quarterCol = 'bg-gray-50 dark:bg-gray-800/40';

export function RevenueMatrixView({ data, columns, lens }: RevenueMatrixViewProps) {
  const allRestaurants = data.clients.flatMap((c) => c.restaurants);
  const allMonths = data.months;
  const grandTotal = sumRestaurants(allRestaurants, allMonths, lens).omzet;
  const shareOf = (omzet: number) => (grandTotal > 0 ? omzet / grandTotal : null);

  return (
    <table className="w-full text-sm border-collapse">
      <thead>
        <tr className="border-b border-gray-200 dark:border-gray-700">
          <th className={cn('text-left font-medium px-3 py-2 sticky left-0 bg-white dark:bg-gray-900', secondaryText)}>
            Client / Restaurant
          </th>
          {columns.map((col) => (
            <th
              key={col.key}
              className={cn('text-right font-medium px-3 py-2 whitespace-nowrap', secondaryText, col.type === 'quarter' && quarterCol)}
            >
              {col.label}
            </th>
          ))}
          <th className={cn('text-right font-medium px-3 py-2', secondaryText)}>Total</th>
          <th className={cn('text-right font-medium px-3 py-2', secondaryText)}>% of total</th>
        </tr>
      </thead>
      <tbody>
        {data.clients.map((client) => {
          const clientTotal = sumRestaurants(client.restaurants, allMonths, lens).omzet;
          return (
            <Fragment key={client.clientId}>
              <tr className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
                <td className={cn('px-3 py-2 sticky left-0 bg-gray-50 dark:bg-gray-800/50', primaryText, semiBoldText)}>
                  {client.clientName}
                </td>
                {columns.map((col) => (
                  <td key={col.key} className={cn('px-3 py-2 text-right', semiBoldText, primaryText, col.type === 'quarter' && quarterCol)}>
                    {formatEuro(sumRestaurants(client.restaurants, col.months, lens).omzet)}
                  </td>
                ))}
                <td className={cn('px-3 py-2 text-right', semiBoldText, primaryText)}>{formatEuro(clientTotal)}</td>
                <td className={cn('px-3 py-2 text-right', semiBoldText, secondaryText)}>{formatPct(shareOf(clientTotal))}</td>
              </tr>

              {client.restaurants.map((r) => {
                const rTotal = sumMonths(r.months, allMonths, lens).omzet;
                return (
                  <tr key={r.restaurantId} className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/30">
                    <td className={cn('px-3 py-2 sticky left-0 bg-white dark:bg-gray-900', smallTextSize, primaryText)}>
                      <span className="pl-5 block">{r.restaurantName}</span>
                    </td>
                    {columns.map((col) => (
                      <td key={col.key} className={cn('px-3 py-2 text-right', smallTextSize, primaryText, col.type === 'quarter' && quarterCol)}>
                        {formatEuro(sumMonths(r.months, col.months, lens).omzet)}
                      </td>
                    ))}
                    <td className={cn('px-3 py-2 text-right', smallTextSize, semiBoldText, primaryText)}>{formatEuro(rTotal)}</td>
                    <td className={cn('px-3 py-2 text-right', smallTextSize, secondaryText)}>{formatPct(shareOf(rTotal))}</td>
                  </tr>
                );
              })}
            </Fragment>
          );
        })}

        <tr className="border-t-2 border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-800">
          <td className={cn('px-3 py-2 sticky left-0 bg-gray-100 dark:bg-gray-800', primaryText, semiBoldText)}>TOTAL</td>
          {columns.map((col) => (
            <td key={col.key} className={cn('px-3 py-2 text-right', semiBoldText, primaryText, col.type === 'quarter' && quarterCol)}>
              {formatEuro(sumRestaurants(allRestaurants, col.months, lens).omzet)}
            </td>
          ))}
          <td className={cn('px-3 py-2 text-right', semiBoldText, primaryText)}>{formatEuro(grandTotal)}</td>
          <td className={cn('px-3 py-2 text-right', semiBoldText, secondaryText)}>{grandTotal > 0 ? '100%' : '—'}</td>
        </tr>
      </tbody>
    </table>
  );
}
